// STARWEAVE — modular ship system.
// A ship is an assembly of interchangeable PARTS (cockpit, fuselage, wings,
// engines), each a procedural mesh with stats + a fixed snap slot. Swapping
// any part yields a new ship; the starting ship is a seeded-random valid
// assembly. Parts are the unit of progression (collected/crafted from world
// resources later). Forward = +X.

import * as THREE from 'three';

// Deterministic integer hash / RNG (matches the project's determinism ethos).
function hash32(x: number): number {
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}
export class Rng {
  private s: number;
  constructor(seed: number) { this.s = seed | 0; }
  next(): number { this.s = (this.s + 0x9e3779b9) | 0; return hash32(this.s) / 4294967295; }
  int(n: number): number { return Math.floor(this.next() * n); }
  pick<T>(arr: T[]): T { return arr[this.int(arr.length)]; }
  range(a: number, b: number): number { return a + this.next() * (b - a); }
}

export interface PartStats {
  mass: number;
  thrust?: number;
  cargo?: number;
  shield?: number;
  agility?: number;
}
interface Mats {
  hull: THREE.Material;
  accent: THREE.Material;
  glass: THREE.Material;
  glow: THREE.Material;
}
interface PartDef {
  id: string;
  stats: PartStats;
  build: (m: Mats) => THREE.Object3D;
}

function mats(accent: THREE.Color, hullTex?: THREE.Texture): Mats {
  if (hullTex) {
    hullTex.wrapS = hullTex.wrapT = THREE.RepeatWrapping;
    hullTex.colorSpace = THREE.SRGBColorSpace;
    hullTex.repeat.set(3, 3);
  }
  return {
    hull: new THREE.MeshStandardMaterial({ color: 0xcdd5e0, metalness: 0.92, roughness: 0.42, map: hullTex }),
    accent: new THREE.MeshStandardMaterial({ color: accent, metalness: 0.65, roughness: 0.28, map: hullTex }),
    glass: new THREE.MeshStandardMaterial({ color: 0x0e3a4a, metalness: 0.1, roughness: 0.05, emissive: 0x0c4055, emissiveIntensity: 0.6, transparent: true, opacity: 0.85 }),
    glow: new THREE.MeshStandardMaterial({ color: 0x111111, emissive: accent, emissiveIntensity: 2.4 }),
  };
}

const mesh = (g: THREE.BufferGeometry, mat: THREE.Material) => new THREE.Mesh(g, mat);

// ---- Fuselages (define the spine; snap at origin, length along X) ----------
const FUSELAGES: PartDef[] = [
  {
    id: 'fuselage-sleek', stats: { mass: 5, cargo: 8, shield: 2 },
    build: (m) => {
      const pts: THREE.Vector2[] = [];
      for (let i = 0; i <= 12; i++) { const t = i / 12; pts.push(new THREE.Vector2(Math.sin(t * Math.PI) * 1.3 * (1 - t * 0.3) + 0.05, (t - 0.5) * 11)); }
      const g = new THREE.LatheGeometry(pts, 18);
      const o = mesh(g, m.hull); o.rotation.z = Math.PI / 2; return o;
    },
  },
  {
    id: 'fuselage-hauler', stats: { mass: 8, cargo: 18, shield: 3 },
    build: (m) => {
      const o = new THREE.Group();
      o.add(mesh(new THREE.CapsuleGeometry(1.7, 7, 6, 16), m.hull).rotateZ(Math.PI / 2));
      const ring = mesh(new THREE.TorusGeometry(1.75, 0.18, 8, 20), m.accent); ring.rotation.y = Math.PI / 2; o.add(ring);
      return o;
    },
  },
  {
    id: 'fuselage-interceptor', stats: { mass: 4, cargo: 5, shield: 1, agility: 2 },
    build: (m) => {
      const o = new THREE.Group();
      const body = mesh(new THREE.CylinderGeometry(0.7, 1.4, 11, 8), m.hull); body.rotation.z = Math.PI / 2; o.add(body);
      o.add(mesh(new THREE.ConeGeometry(0.7, 2.2, 8), m.hull).translateX(6.5).rotateZ(-Math.PI / 2));
      return o;
    },
  },
];

// ---- Cockpits (snap near the nose) -----------------------------------------
const COCKPITS: PartDef[] = [
  {
    id: 'cockpit-dome', stats: { mass: 1.5, shield: 2 },
    build: (m) => { const o = mesh(new THREE.SphereGeometry(1.1, 18, 12, 0, Math.PI * 2, 0, Math.PI / 2), m.glass); o.rotation.x = Math.PI; o.position.y = 0.2; return o; },
  },
  {
    id: 'cockpit-canopy', stats: { mass: 1.2, shield: 1, agility: 1 },
    build: (m) => { const o = mesh(new THREE.SphereGeometry(0.95, 16, 10), m.glass); o.scale.set(1.6, 0.8, 1); o.position.y = 0.5; return o; },
  },
  {
    id: 'cockpit-pod', stats: { mass: 2, shield: 3 },
    build: (m) => { const o = new THREE.Group(); o.add(mesh(new THREE.IcosahedronGeometry(1.1, 1), m.hull)); o.add(mesh(new THREE.SphereGeometry(0.6, 12, 8), m.glass).translateX(0.7).translateY(0.2)); return o; },
  },
];

// ---- Wings (built as a mirrored pair) --------------------------------------
const WINGS: PartDef[] = [
  {
    id: 'wing-delta', stats: { mass: 2, agility: 4, thrust: 1 },
    build: (m) => {
      const s = new THREE.Shape(); s.moveTo(0, 0); s.lineTo(-3.5, 4.2); s.lineTo(-4.2, 4.2); s.lineTo(-0.6, -0.4); s.closePath();
      const g = new THREE.ExtrudeGeometry(s, { depth: 0.18, bevelEnabled: true, bevelSize: 0.08, bevelThickness: 0.06, bevelSegments: 1 });
      const o = mesh(g, m.hull); o.rotation.x = Math.PI / 2; return o;
    },
  },
  {
    id: 'wing-swept', stats: { mass: 1.5, agility: 6 },
    build: (m) => { const o = mesh(new THREE.BoxGeometry(2.2, 0.16, 4.4), m.hull); o.position.set(-1, 0, 2.2); o.rotation.y = 0.5; o.add(mesh(new THREE.BoxGeometry(0.16, 0.5, 1.4), m.accent).translateZ(2)); return o; },
  },
  {
    id: 'wing-stub', stats: { mass: 3, shield: 3, agility: 1 },
    build: (m) => { const o = mesh(new THREE.BoxGeometry(2.6, 0.4, 1.6), m.hull); o.position.set(-0.5, 0, 1.4); return o; },
  },
];

// ---- Engines (mirrored pair, glowing nozzles) ------------------------------
const ENGINES: PartDef[] = [
  {
    id: 'engine-twin', stats: { mass: 3, thrust: 10 },
    build: (m) => { const o = new THREE.Group(); o.add(mesh(new THREE.CylinderGeometry(0.55, 0.65, 2.4, 12), m.hull).rotateZ(Math.PI / 2)); o.add(mesh(new THREE.ConeGeometry(0.5, 0.9, 12), m.glow).translateX(-1.4).rotateZ(Math.PI / 2)); return o; },
  },
  {
    id: 'engine-ion', stats: { mass: 2, thrust: 7, agility: 2 },
    build: (m) => { const o = new THREE.Group(); o.add(mesh(new THREE.CylinderGeometry(0.4, 0.4, 3, 10), m.hull).rotateZ(Math.PI / 2)); const ring = mesh(new THREE.TorusGeometry(0.55, 0.12, 6, 14), m.glow); ring.position.x = -1.5; ring.rotation.y = Math.PI / 2; o.add(ring); return o; },
  },
  {
    id: 'engine-heavy', stats: { mass: 6, thrust: 16, agility: -1 },
    build: (m) => { const o = new THREE.Group(); o.add(mesh(new THREE.CylinderGeometry(0.8, 1.0, 2.6, 14), m.hull).rotateZ(Math.PI / 2)); o.add(mesh(new THREE.CylinderGeometry(0.7, 0.5, 0.8, 14), m.glow).translateX(-1.6).rotateZ(Math.PI / 2)); return o; },
  },
];

const ACCENTS = [0x33ddff, 0xff7a1a, 0xc060ff, 0x40ff90, 0xffd23f, 0xff4d6d];

export interface ShipBuild {
  group: THREE.Group;
  seed: number;
  accent: number;
  parts: { slot: string; id: string }[];
  stats: { mass: number; thrust: number; cargo: number; shield: number; agility: number };
}

/** Deterministically assemble a modular ship from a seed. */
export function assembleShip(seed: number, hullTex?: THREE.Texture): ShipBuild {
  const r = new Rng(seed);
  const accent = new THREE.Color(r.pick(ACCENTS));
  const m = mats(accent, hullTex);

  const fuselage = r.pick(FUSELAGES);
  const cockpit = r.pick(COCKPITS);
  const wing = r.pick(WINGS);
  const engine = r.pick(ENGINES);

  const group = new THREE.Group();

  const fObj = fuselage.build(m); group.add(fObj);

  const cObj = cockpit.build(m); cObj.position.x = 3.4; group.add(cObj);

  // mirrored wing pair
  for (const side of [1, -1]) {
    const w = wing.build(m); w.position.set(-0.5, 0, 0); w.scale.z = side; group.add(w);
  }
  // mirrored engine pair
  for (const side of [1, -1]) {
    const e = engine.build(m); e.position.set(-5.2, -0.1, side * 1.1); group.add(e);
  }

  // Running-light strips down each flank (glow + bloom).
  for (const side of [1, -1]) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(7, 0.1, 0.1), m.glow);
    strip.position.set(-0.6, 0.18, side * 0.95);
    group.add(strip);
  }

  // aggregate stats
  const stats = { mass: 0, thrust: 0, cargo: 0, shield: 0, agility: 0 };
  const acc = (s: PartStats, n = 1) => {
    stats.mass += (s.mass || 0) * n;
    stats.thrust += (s.thrust || 0) * n;
    stats.cargo += (s.cargo || 0) * n;
    stats.shield += (s.shield || 0) * n;
    stats.agility += (s.agility || 0) * n;
  };
  acc(fuselage.stats); acc(cockpit.stats); acc(wing.stats, 2); acc(engine.stats, 2);
  // derived agility: base + wing/engine bonus, penalized by mass
  stats.agility = Math.max(1, Math.round(stats.agility + (stats.thrust / Math.max(1, stats.mass)) * 4));

  return {
    group, seed, accent: accent.getHex(),
    parts: [
      { slot: 'fuselage', id: fuselage.id },
      { slot: 'cockpit', id: cockpit.id },
      { slot: 'wings', id: wing.id },
      { slot: 'engines', id: engine.id },
    ],
    stats: {
      mass: Math.round(stats.mass),
      thrust: Math.round(stats.thrust),
      cargo: Math.round(stats.cargo),
      shield: Math.round(stats.shield),
      agility: stats.agility,
    },
  };
}
